import React, { FC, useMemo } from 'react';
import {
  FormControlProps,
  FormHelperText,
  MenuItem,
  Select,
  styled,
  useTheme,
} from '@material-ui/core';
import { CustomInput } from './CustomForm';
import { uniqBy } from 'lodash';
import FilterFormControl from './CustomForm/FilterFormControl';
import FilterInputLabel from './CustomForm/FilterInputLabel';
import { SpriteIcon, SpriteIconsLight } from 'src/config/api/models/FontAwesome';
import { FontAwesomeSpriteIcon } from './FontAwesomeLocal';

export type FilterOption = {
  id: string;
  label: string;
};

const StyledIcon = styled(FontAwesomeSpriteIcon)({
  position: 'absolute',
  right: 0,
  pointerEvents: 'none',
});

type FilterProps = Omit<
  FormControlProps,
  'id' | 'label' | 'value' | 'onChange' | 'options' | 'emptyOption' | 'disabled' | 'placeholder'
> & {
  id: string;
  label?: string;
  value?: string;
  onChange: (value: string) => void;
  options: FilterOption[];
  emptyOption?: boolean;
  disabled?: boolean;
  placeholder?: string;
  helperText?: string;
  icon?: SpriteIcon;
};

const Filter: FC<FilterProps> = ({
  id,
  label,
  value = '',
  icon,
  onChange,
  options,
  emptyOption,
  placeholder,
  helperText,
  disabled,
  ...props
}) => {
  const labelId: string = useMemo(() => `${id}-label`, [id]);
  const filteredOptions = uniqBy(options, ({ id }) => id);
  const theme = useTheme();

  return (
    <FilterFormControl className={`filter ${disabled ? 'disabled' : ''}`} {...props}>
      {icon && (
        <FontAwesomeSpriteIcon iconSize="lg" icon={icon} color={theme.palette.secondary.main} />
      )}
      {label && <FilterInputLabel id={labelId}>{label}</FilterInputLabel>}
      <Select
        id={id}
        labelId={labelId}
        input={<CustomInput />}
        value={value}
        onChange={(event) => onChange(event.target.value as string)}
        displayEmpty={emptyOption}
        disabled={disabled || filteredOptions.length === 0 ? true : false}
        IconComponent={() => (
          <StyledIcon icon={SpriteIconsLight.CHEVRON_DOWN} color={theme.palette.secondary.main} />
        )}
      >
        {emptyOption && <MenuItem value="">{placeholder || ' '}</MenuItem>}
        {filteredOptions.map(({ id: optionId, label }) => (
          <MenuItem key={optionId} value={optionId}>
            {label}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>{helperText}</FormHelperText>
    </FilterFormControl>
  );
};

export default Filter;
